
export const parseCharacterBible = (characterBibleContent) => {
  const characterBible = {};

  const strategies = [
    (content) => content.split(/\n(?=\d+\.\s)/),
    (content) => content.split('\n\n'),
    (content) => content.split(/\n\nCharacter \d+:\s/).filter(entry => entry.trim() !== '')
  ];

  for (const strategy of strategies) {
    const characterEntries = strategy(characterBibleContent);

    characterEntries.forEach(entry => {
      const lines = entry.split('\n');
      const nameLine = lines[0].trim();

      let name = '';
      let details = '';

      if (strategy === strategies[0]) {
        const nameMatch = nameLine.match(/\d+\.\s*(.+):/);
        if (nameMatch && lines.length > 1) {
          name = nameMatch[1].trim();
          details = lines.slice(1).join(' ').trim();
        }
      } else if (strategy === strategies[1]) {
        if (nameLine.endsWith(':')) {
          name = nameLine.slice(0, -1).trim();
          details = lines.slice(1).join(' ').trim();
        }
      } else if (strategy === strategies[2]) {
        [name] = nameLine.split(':');
        details = lines.slice(1).join(' ').trim();
      }

      if (name && details) {
        characterBible[name] = details;
      }
    });

    if (Object.keys(characterBible).length > 0) {
      return characterBible;
    }
  }

  return characterBible;
};

/*export const formatCharacterBible = (characters, characterBible) => {
  return characters.map(name => {
    const details = characterBible[name.replace(/[*-]/g, '').trim()];
    return `${name}: ${details || 'No details available'}`;
  }).join(', ');
};*/

export const formatCharacterBible = (characters, characterBible, options = {}) => {
  const { includeFields = null, excludeFields = [] } = options;

  return characters.map(characterName => {
    const details = characterBible[characterName];
    if (details) {
      // Filter fields based on options
      const detailsString = Object.entries(details)
        .filter(([key]) =>
          (!includeFields || includeFields.includes(key)) &&
          !excludeFields.includes(key)
        )
        .map(([key, value]) => {
          if (typeof value === 'object' && value !== null) {
            return `${key}: { ${Object.entries(value).map(([subKey, subValue]) => `${subKey}: ${subValue}`).join(', ')} }`;
          }
          return `${key}: ${value}`;
        })
        .join(', ');

      return `${characterName}: ${detailsString}`;
    }
    return `${characterName}: Details not found`;
  }).join(' | ');
};

export const formatCharacterDetails = (details) => {
  return Object.entries(details)
    .map(([key, value]) => `${key}: ${value}`)
    .join('\n');
};

export const formatCharacterBibleOnRegeneration = (characters, characterBible) => {
  return Object.entries(characters).map(([name, details]) => {
    const formattedName = name.replace(/[*-]/g, '').trim();
    const characterDetails = characterBible[formattedName] || 'No details available';
    return `${formattedName}: ${characterDetails}`;
  }).join(', ');
};

/* export const transformCharacterBibleForSlideshow = (characterBible) => {
  return Object.entries(characterBible).map(([name, details]) => {
    return {
      name: name,
      details: details.split('    ').reduce((acc, item) => {
        const [key, value] = item.split(':');
        if (key && value) {
          acc[key.trim()] = value.trim();
        }
        return acc;
      }, {})
    };
  });
}; */

export const transformCharacterBibleForSlideshow = (characterBible) => {
  return Object.entries(characterBible).reduce((acc, [name, details]) => {
    acc[name] = details.split('    ').reduce((detailsAcc, item) => {
      const [key, value] = item.split(':');
      if (key && value) {
        detailsAcc[key.trim()] = value.trim();
      }
      return detailsAcc;
    }, { name: name }); // Add the name as an attribute within the details object
    return acc;
  }, {});
};


export const transformCharacterBible = (characterBible) => {
  return Object.entries(characterBible).reduce((acc, [name, details]) => {
    const sections = details.split(/\s{2,}/).filter(section => section.trim());
    const detailsObj = { name: name };

    sections.forEach(section => {
      const [key, value] = section.split(/:\s*(.+)/);

      if (key && value) {
        const trimmedKey = key.trim();
        const trimmedValue = value.trim();

        // Check if the value contains nested details
        if (trimmedValue.includes('\n')) {
          const subFields = trimmedValue.split('\n').reduce((subAcc, subField) => {
            const [subKey, subValue] = subField.split(/:\s*(.+)/);
            if (subKey && subValue) {
              subAcc[subKey.trim()] = subValue.trim();
            }
            return subAcc;
          }, {});

          detailsObj[trimmedKey] = subFields;
        } else {
          detailsObj[trimmedKey] = trimmedValue;
        }
      } else if (key) {
        detailsObj[key.trim()] = '';
      }
    });

    acc[name] = detailsObj;
    return acc;
  }, {});
};

const jlog = (event, data) => {
  //  try { console.log(`[parseScenes] ${event} ${JSON.stringify(data)}`); }
  //  catch { console.log(`[parseScenes] ${event} <json_error>`); }
};

// ---- utility.js ------------------------------------------------------------

/**
 * parseScenesFromDetailedScreenplay(text, opts?)
 * Robust line-scanner for your screenplay format with deep logging.
 * - Skips "VFX / SFX / Plates"
 * - Accepts both "Visual Prompt" and "VisualPrompt", etc.
 * - Preserves multi-line content per section.
 */
export function parseScenesFromDetailedScreenplay(inputText, opts = {}) {
  const PARSER_ID = "scan-v3-verbose";
  const {
    logPrefix = "[parseScenes]",
    debug = true,
    logInputPreviewChars = 400, // how much of the raw input to preview in logs
    logUnmappedLimit = 3,       // limit "unmapped line" noise
  } = opts;


  // ---- 0) Safety + input diagnostics --------------------------------------
  try {
    if (typeof inputText !== "string") {
      jlog("start.invalidInput", { type: typeof inputText });
      return { scenes: [], meta: { parser: PARSER_ID, error: "Input is not a string." } };
    }

    const rawPreview =
      inputText.slice(0, logInputPreviewChars).replace(/\n/g, "⏎").replace(/\r/g, "⏎");
    jlog("start", {
      parser: PARSER_ID,
      textLen: inputText.length,
      inputPreview: rawPreview + (inputText.length > logInputPreviewChars ? "…" : ""),
    });

    // ---- 1) Normalize whitespace (keeps structure; removes exotic spaces) ---
    const NBSP = /\u00A0/g; // non-breaking space
    const UNI_SPACES = /[\u1680\u180E\u2000-\u200B\u202F\u205F\u3000]/g;
    let text = inputText.replace(/\r\n?/g, "\n").replace(NBSP, " ").replace(UNI_SPACES, " ");
    const normPreview =
      text.slice(0, logInputPreviewChars).replace(/\n/g, "⏎");
    jlog("normalize", { normalizedLen: text.length, preview: normPreview + (text.length > logInputPreviewChars ? "…" : "") });

    // ---- 2) Find scene blocks ("Scene <num>:") ------------------------------
    const sceneRegex = /^[ \t]*Scene[ \t]+(\d+)[ \t]*:/gmi;
    let match;
    const markers = [];
    while ((match = sceneRegex.exec(text)) !== null) {
      markers.push({ number: match[1], index: match.index });
    }
    // If no "Scene X:" markers exist, treat whole text as a single scene block
    if (markers.length === 0) {
      markers.push({ number: "1", index: 0 });
    }

    // Add an end sentinel to slice the last block
    markers.push({ number: "END", index: text.length });

    jlog("markers", { parser: PARSER_ID, sceneMarkers: markers.length - 1, markers: markers.slice(0, -1) });

    // ---- 3) Header synonyms map --------------------------------------------
    // Left side: canonical key -> array of acceptable labels at line start
    const HEADER_MAP = {
      Title: ["Title", "Scene Title"],
      Duration: ["Duration"],
      SceneSynopsis: ["Scene Synopsis", "SceneSynopsis", "Synopsis", "Scene Outline", "SceneOutline", "Outline"],
      VisualPrompt: ["Visual Prompt", "VisualPrompt"],
      SettingDetails: ["Setting Details", "SettingDetails"],
      Environment: ["Environment", "Env"],
      CharactersAndExpressions: ["Characters and Expressions", "Characters & Expressions", "Characters/Expressions", "Character Expressions"],
      CharacterAttire: ["Character Attire"],
      DialogueScript: ["Dialogue Script", "Dialogue", "DialogueScript"],
      //BeatSummary: ["Beat Summary", "BeatSummary", "Beats"],
      //ShotsPerBeat: ["Shots Per Beat", "ShotsPerBeat", "Shots", "Shot List"],
      ShotMatrix: ["Shot Matrix", "ShotMatrix"],
      CoveragePlan: ["Coverage Plan", "Coverage", "CoveragePlan"],
      LightingPlan: ["Lighting Plan", "Lighting", "LightingPlan"],
      BackgroundMusic: ["Background Music", "BackgroundMusic", "Music"],
      CharacterList: ["Character List", "CharacterList", "Characters"],
      FX: ["FX"],
      SceneBible: ["Scene Bible", "SceneBible"],
    };

    // Build a fast lookup from label -> canonical key
    const labelToKey = new Map();
    Object.entries(HEADER_MAP).forEach(([key, labels]) => {
      labels.forEach((label) => {
        labelToKey.set(label.toLowerCase(), key);
      });
    });

    // For quick disambiguation when the line starts with "Characters"
    const isCharactersAndExpressionsLabel = (s) =>
      HEADER_MAP.CharactersAndExpressions.some((l) => equalsHeader(s, l));
    const isCharactersAttireLabel = (s) =>
      HEADER_MAP.CharacterAttire.some((l) => equalsHeader(s, l));
    const isCharacterListLabel = (s) =>
      HEADER_MAP.CharacterList.some((l) => equalsHeader(s, l));

    // ---- Helpers ------------------------------------------------------------
    function equalsHeader(line, label) {
      const l = line.trimStart();
      return l.toLowerCase().startsWith(label.toLowerCase() + ":");
    }
    function whichHeader(line) {
      const trimmed = line.trimStart();
      const idx = trimmed.indexOf(":");
      if (idx <= 0) return null;
      const label = trimmed.slice(0, idx).trim();
      // Disambiguate "Characters ..." collisions
      if (isCharactersAndExpressionsLabel(trimmed)) return "CharactersAndExpressions";
      if (isCharactersAttireLabel(trimmed)) return "CharacterAttire";
      if (isCharacterListLabel(trimmed)) return "CharacterList";
      const key = labelToKey.get(label.toLowerCase());
      return key || null;
    }
    function pushOrAppend(map, key, line) {
      if (!map[key]) map[key] = [];
      map[key].push(line);
    }

    function collectUntilNextHeader(lines, startIdx) {
      const collected = [];
      let i = startIdx;

      for (; i < lines.length; i++) {
        const raw = lines[i];

        // New scene? Stop.
        if (/^[ \t]*Scene[ \t]+\d+[ \t]*:/i.test(raw)) break;
        if (whichHeader(raw)) break;
        collected.push(raw);
      }

      return { collected, nextIdx: i };
    }
    function joinBlock(blockLines) {
      // Preserve newlines exactly as they appeared (minus leading header line)
      return blockLines.join("\n").trim();
    }

    // ---- 4) Parse each block ------------------------------------------------
    const scenes = [];
    let unmappedLogged = 0;

    for (let m = 0; m < markers.length - 1; m++) {
      const start = markers[m].index;
      const end = markers[m + 1].index;
      const number = markers[m].number;

      // Slice block and log a preview
      const block = text.slice(start, end);
      const blockPreview = block.slice(0, 140).replace(/\n/g, "⏎");
      jlog("block.captured", { number, rawLen: block.length, rawPreview: blockPreview + (block.length > 140 ? "…" : "") });

      // Split lines for scanning
      const lines = block.split("\n");

      // For reporting
      const captured = {
        Title: "",
        Duration: "",
        SceneSynopsis: "",
        VisualPrompt: "",
        SettingDetails: "",
        Environment: "",
        CharactersAndExpressions: "",
        CharacterAttire: "",
        DialogueScript: "",
        //BeatSummary: "",
        //ShotsPerBeat: "",
        ShotMatrix: "",
        CoveragePlan: "",
        LightingPlan: "",
        BackgroundMusic: "",
        CharacterList: "",
        FX: "",
        SceneBible: "",
      };

      let currentKey = null;

      // Walk the lines
      for (let i = 0; i < lines.length; i++) {
        let line = lines[i];

        // Skip empty lines unless they belong to an active section
        if (!currentKey && line.trim().length === 0) continue;

        // Scene header (e.g., "Scene 3:") -> ignore
        if (/^[ \t]*Scene[ \t]+\d+[ \t]*:/i.test(line)) {
          const trimmed = line.trim();
          jlog("scan.sceneHeader", { number, atLine: i + 1, text: trimmed });
          // If the title is inline after "Scene N:", capture it
          const afterColon = trimmed.replace(/^[ \t]*Scene[ \t]+\d+[ \t]*:/i, "").trim();
          if (afterColon && !captured.Title) {
            captured.Title = afterColon;
            jlog("scene.inlineTitle", { number, title: captured.Title });
          }
          currentKey = null;
          continue;
        }

        // New known header?
        const key = whichHeader(line);
        if (key) {
          const trimmed = line.trimStart();
          const rest = trimmed.slice(trimmed.indexOf(":") + 1); // inline content after colon
          jlog("scan.header", { number, atLine: i + 1, label: key, raw: trimmed });

          // Collect this line's inline content + the following lines until the next header
          const blockLines = [];
          if (rest && rest.trim().length > 0) blockLines.push(rest);

          const { collected, nextIdx } = collectUntilNextHeader(lines, i + 1);
          blockLines.push(...collected);
          const blockText = joinBlock(blockLines);

          // Only set/log if the block actually has content
          if (blockText.length > 0) {
            captured[key] = blockText;
            jlog("getBlock.hit", {
              header: key,
              lines: blockLines.length,
              len: blockText.length,
              preview: blockText.slice(0, 120),
            });
          }
          currentKey = key;
          i = nextIdx - 1;
          continue;
        }

        // Not a header line; if we’re currently inside a section, append it
        if (currentKey) {
          captured[currentKey] = (captured[currentKey] + "\n" + line).trim();
          continue;
        }

        // Unmapped-but-Header-looking noise (keep small)
        if (unmappedLogged < logUnmappedLimit && /^[ \t]*[A-Z][A-Za-z0-9 /&()’'--]+:/.test(line)) {
          jlog("scan.unmapped", { number, atLine: i + 1, raw: line.trim() });
          unmappedLogged++;
        }
      }

      // ---- 5) Post-process per scene ---------------------------------------
      // Title & Duration sometimes inline in the first lines—if still empty, try cheap fallbacks
      if (!captured.Title) {
        const firstContent = lines.find(l =>
          l.trim() &&
          !/^[ \t]*Scene[ \t]+\d+[ \t]*:/i.test(l) &&
          !whichHeader(l)
        );
        const t = lines.find((l) => equalsHeader(l, "Title"));
        captured.Title = (t ? t.split(":").slice(1).join(":").trim() : (firstContent ? firstContent.trim() : ""));

      }
      if (!captured.Duration) {
        const d = lines.find((l) => equalsHeader(l, "Duration"));
        if (d) captured.Duration = d.split(":").slice(1).join(":").trim();
      }

      // Derive Character List if missing
      let derivedCharacters = [];
      if (captured.CharacterList) {
        derivedCharacters = captured.CharacterList
          .split(/[,;\n]/)
          .map((s) => s.trim())
          .filter(Boolean);
      } else {
        // Try from Characters & Expressions (names ending with ":")
        const fromExpr = (captured.CharactersAndExpressions || "")
          .split("\n")
          .map((l) => (l.match(/^\s*([A-Z][A-Za-z0-9]+)\s*:/) || [null, null])[1])
          .filter(Boolean);
        const fromAttire = (captured.CharacterAttire || "")
          .split("\n")
          .map((l) => (l.match(/^\s*([A-Z][A-Za-z0-9]+)\s*:/) || [null, null])[1])
          .filter(Boolean);
        const fromDialogue = (captured.DialogueScript || "")
          .split("\n")
          .map((l) => (l.match(/^\s*([A-Z][A-Za-z0-9]+)\s*:/) || [null, null])[1])
          .filter(Boolean);
        derivedCharacters = Array.from(new Set([...fromExpr, ...fromAttire, ...fromDialogue]));
      }

      // Quick metrics
      const beatSummaryCount = (captured.BeatSummary || "")
        .split("\n")
        .filter((l) => /^\s*\d+[\)\.\-]/.test(l)).length;
      const shotsPerBeatCount = (captured.ShotsPerBeat || "")
        .split("\n")
        .filter((l) => /^\s*Beat\s+\d+/.test(l)).length;

      scenes.push({
        number,
        ...captured,
        Characters: derivedCharacters,
        _metrics: {
          beatSummaryCount,
          shotsPerBeatCount,
        },
      });

      jlog("scene.push", { number, idx: scenes.length - 1 });
    }

    jlog("done", { scenes: scenes.length, parser: PARSER_ID });

    const toLines = (txt) =>
      (txt || '')
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean);
    const parseCoveragePlan = (txt) => {
      const out = { coverage: '', transitions: '', editorialIntent: '' };
      toLines(txt).forEach(line => {
        const [k, ...rest] = line.split(':');
        const key = (k || '').toLowerCase().replace(/\s+/g, '');
        const val = rest.join(':').trim();
        if (key === 'coverage') out.coverage = val;
        else if (key === 'transitions') out.transitions = val;
        else if (key === 'editorialintent') out.editorialIntent = val;
        // If there’s a free-form first line with no key, treat it as coverage
        else if (!out.coverage) out.coverage = line.trim();
      });
      return out;
    };    // NEW: Scene Bible -> object, like CoveragePlan
    const parseSceneBible = (txt) => {
      if (!txt) return null;
      const out = {
        lens_mm: null,
        aspect: '',
        time_of_day: '',
        palette: '',
        grade: '',
        continuity_notes: [],
      };
      toLines(txt).forEach(line => {
        const [k, ...rest] = line.split(':');
        const key = (k || '').toLowerCase().trim();
        const val = rest.join(':').trim();
        if (!key) return;
        if (key.startsWith('lens')) {
          const n = parseFloat(val);
          out.lens_mm = Number.isFinite(n) ? n : (val || null);
        } else if (key === 'aspect') {
          out.aspect = val;
        } else if (key === 'time') {
          out.time_of_day = val;
        } else if (key === 'palette') {
          out.palette = val;
        } else if (key === 'grade') {
          out.grade = val;
        } else if (key === 'continuity') {
          out.continuity_notes = val.split(';').map(s => s.trim()).filter(Boolean);
        }
      });
      // if all fields empty, return null to match your null/empty style
      const hasAny = out.lens_mm || out.aspect || out.time_of_day || out.palette || out.grade || (out.continuity_notes && out.continuity_notes.length);
      return hasAny ? out : null;
    };

    // NEW: Shot Matrix -> list of lines, like ShotsPerBeat
    const parseShotMatrix = (txt) => toLines(txt);

    const normalized = scenes.map(s => ({
      number: Number(s.number) || s.number,
      title: (s.Title || '').trim(),
      duration: (s.Duration || '').trim(),
      synopsis: (s.SceneSynopsis || '').trim(),
      visualPrompt: s.VisualPrompt || '',
      settingDetails: s.SettingDetails || '',
      environment: s.Environment || '',
      // keep both spellings to be extra safe with downstream access
      characterExperessions: toLines(s.CharactersAndExpressions || ''),
      characterExpressions: toLines(s.CharactersAndExpressions || ''),
      // keep both spellings to be extra safe with downstream access
      characterAttire: toLines(s.CharacterAttire || ''),


      dialogues: toLines(s.DialogueScript || ''),
      backgroundMusic: s.BackgroundMusic || '',
      characters: Array.isArray(s.Characters) ? s.Characters : [],
      //beatSummary: parseBeatSummary(s.BeatSummary || ''),
      //shotsPerBeat: parseShotsPerBeat(s.ShotsPerBeat || ''),
      shotMatrix: parseShotMatrix(s.ShotMatrix || ''),
      coveragePlan: parseCoveragePlan(s.CoveragePlan || ''),
      lightingPlan: s.LightingPlan || '',
      sceneBible: parseSceneBible(s.SceneBible || ''),
      //fx: (s.FX || '').trim(),
    }));

    return normalized.slice(0, 15);

  } catch (err) {
    // Defensive: detailed error logs with location if any
    const safeMsg = (err && (err.stack || err.message)) ? String(err.stack || err.message) : String(err);
    console.error(`${logPrefix}`, "fatal", { parser: PARSER_ID, error: safeMsg });
    return { scenes: [], meta: { parser: PARSER_ID, error: safeMsg } };
  }
}



export const downloadToBlob = async (url) => {
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch ${url}`);
  }
  return await response.blob();
};

export const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(blob);
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
  });
};

export const base64ToBlob = async (base64) => {
  const res = await fetch(base64);
  return await res.blob();
};

export const parseCharacterBible2 = (characterBibleString) => {
  const characterBible = {};

  // Split by character blocks
  const characterBlocks = characterBibleString.split(/\n(?=\d+\.\s)/);

  characterBlocks.forEach(block => {
    // Extract the character name and remove the number prefix
    const [nameLine, ...detailsLines] = block.split('\n');
    const nameMatch = nameLine.match(/\d+\.\s*(.+):/);

    if (nameMatch) {
      const name = nameMatch[1].trim();
      const detailsObj = { name };

      let currentCategory = null;

      detailsLines.forEach(line => {
        const trimmedLine = line.trim();

        // Check for a new category (e.g., Appearance, Attire, etc.)
        if (/^[A-Za-z\s]+:$/.test(trimmedLine)) {
          currentCategory = trimmedLine.slice(0, -1).trim();
        } else if (currentCategory && trimmedLine) {
          // For nested categories (like under Physical Dimensions or Demographic)
          const [key, ...valueParts] = trimmedLine.split(':');
          const value = valueParts.join(':').trim(); // Join back in case of additional colons

          if (!detailsObj[currentCategory]) {
            detailsObj[currentCategory] = {};
          }

          detailsObj[currentCategory][key.trim()] = value;
        }
      });

      characterBible[name] = detailsObj;
    }
  });

  return characterBible;
}

export const parseCharacterDescriptions = (characterBibleString) => {
  const characterMap = new Map();

  // Ensure the string is trimmed of leading and trailing whitespace
  const trimmedString = characterBibleString.trim();

  // Split the input string by characters that start with "1.", "2.", "3.", etc.
  let characterEntries = trimmedString.split(/\d+\.\s+/);

  // Remove any empty or malformed entries
  characterEntries = characterEntries.filter(entry => entry && entry.trim() !== "");

  characterEntries.forEach((entry, index) => {
    try {
      const lines = entry.split("\n").filter(line => line && line.trim() !== "");

      if (lines.length > 0) {
        const nameLine = lines[0].trim();
        const characterName = nameLine.substring(0, nameLine.length - 1); // Remove the trailing colon

        const characterDescription = lines.slice(1).join("\n");

        characterMap.set(characterName, {
          description: characterDescription
        });
      } else {
        console.warn(`Empty or malformed entry at index ${index}`);
      }
    } catch (error) {
      console.error(`Error processing entry at index ${index}:`, error);
    }
  });

  return characterMap;
};


export const generateCharacterDescriptions = (characters, characterBible) => {
  let result = '';

  characters.forEach(name => {
    if (characterBible.has(name)) {
      result += `${name}:\n${characterBible.get(name).description}\n\n`;
    }
  });

  return result.trim();
}

export const generateCharacterDescriptionsConcise = (characters, characterBible) => {
  let result = '';

  characters.forEach(name => {
    if (characterBible.has(name)) {
      result += `${name}:\n${characterBible.get(name).conciseDescription}\n\n`;
    }
  });

  return result.trim();
}

export function buildDetailedVideoPrompt(
  visualPrompt,
  characterBible,
  environment,
  characterExperessions,
  extras = {} // <-- optional; existing calls with 4 args still work
) {
  const lines = [
    `Scene Visual Prompt: ${visualPrompt}`,
    `Environment: ${environment}`,
    `Character Expressions: ${characterExperessions}`,
    `Character Bible:\n${characterBible}`
  ];

  // Optional enhancements if caller passes them
  const { beats, shotsPerBeat, coveragePlan, lightingPlan, fx } = extras || {};
  if (coveragePlan && (coveragePlan.coverage || coveragePlan.transitions || coveragePlan.editorialIntent)) {
    const parts = [];
    if (coveragePlan.coverage) parts.push(`Coverage: ${coveragePlan.coverage}`);
    if (coveragePlan.transitions) parts.push(`Transitions: ${coveragePlan.transitions}`);
    if (coveragePlan.editorialIntent) parts.push(`Editorial Intent: ${coveragePlan.editorialIntent}`);
    lines.push(parts.join('\n'));
  }
  if (lightingPlan) {
    lines.push(`Lighting Plan:\n${lightingPlan}`);
  }



  return lines.join('\n\n');
}

// ────────────────────────────────────────────────────────────────────────────
// NEW: Scene-bible-aware builders for the image→video per-shot pipeline
// ────────────────────────────────────────────────────────────────────────────
/**
 * Normalize a backend strict-JSON scene into a flat array of { beat, ...shot }
 * where shot has id, size, angle, camera_feel, prompt, micro_motion[], audio[], duration_sec.
 * Accepts either the raw strict JSON shape or a FE-merged scene with .shotMatrix
 */
export function extractShotMatrix(sceneStrictOrMerged) {
  const raw = sceneStrictOrMerged?.shot_matrix || sceneStrictOrMerged?.shotMatrix || [];
  if (!Array.isArray(raw)) return [];
  const out = [];
  raw.forEach(row => {
    const beat = Number(row?.beat) || 0;
    const shots = Array.isArray(row?.shots) ? row.shots : [];
    shots.forEach(s => {
      out.push({
        beat,
        id: String(s?.id || 'A'),
        size: String(s?.size || ''),
        angle: String(s?.angle || ''),
        camera_feel: String(s?.camera_feel || ''),
        prompt: String(s?.prompt || ''),
        transition: String(s?.transition || ''),
        micro_motion: Array.isArray(s?.micro_motion) ? s.micro_motion : _toList(s?.micro_motion),
        audio: Array.isArray(s?.audio) ? s.audio : _toList(s?.audio),
        duration_sec: Number(s?.duration_sec || 8) || 8,
        init_from_prev_last_frame: !!s?.init_from_prev_last_frame
      });
    });
  });
  return out;
}

/** Compact helper: ensure array from string/array/undefined */
function _toList(x) {
  if (!x) return [];
  if (Array.isArray(x)) return x.filter(Boolean).map(String);
  return String(x).split('\n').map(s => s.trim()).filter(Boolean);
}

/**
 * Build a single-frame IMAGE prompt using the scene bible + shot notes.
 * Keep this texty so any image model can digest it.
 */
export function buildImagePromptFromSceneBible(sceneBible = {}, shot = {}) {
  const lens = sceneBible?.lens_mm ? `${sceneBible.lens_mm}mm lens` : null;
  const asp = sceneBible?.aspect ? `aspect ${sceneBible.aspect}` : null;
  const tod = sceneBible?.time_of_day || null;
  const pal = sceneBible?.palette || null;
  const grade = sceneBible?.grade || null;
  const cont = Array.isArray(sceneBible?.continuity_notes) ? sceneBible.continuity_notes.join('; ') : sceneBible?.continuity_notes;

  const header = [
    tod, lens, asp, pal, grade
  ].filter(Boolean).join(', ');

  const shotMeta = [shot.size, shot.angle].filter(Boolean).join(', ');
  const core = shot?.prompt || '';
  const tail = cont ? `Continuity: ${cont}.` : '';

  return [
    header && `[Scene Bible] ${header}.`,
    shotMeta && `[Shot] ${shotMeta}.`,
    core && `[Frame] ${core}`,
    tail
  ].filter(Boolean).join(' ');
}

/**
 * Build the VIDEO motion prompt for a shot (what the I2V should do).
 * We keep it minimal so it pairs with the still image.
 */
export function buildI2VMotionPromptForShot(shot = {}) {
  const mv = shot?.camera_feel ? `Camera: ${shot.camera_feel}.` : '';
  const micro = Array.isArray(shot?.micro_motion) && shot.micro_motion.length
    ? `Micro: ${shot.micro_motion.join(', ')}.`
    : '';
  return [mv, micro].filter(Boolean).join(' ');
}


// Build a compact screenplay text for character-related tasks
export function buildScreenplayLite(screenplayText) {
  try {
    const parsed = parseScenesFromDetailedScreenplay(screenplayText);
    const scenes = Array.isArray(parsed) ? parsed : (Array.isArray(parsed?.scenes) ? parsed.scenes : []);

    const parts = scenes.map(({ number, title, duration, synopsis, characters }) => {
      const t = title ? ` — ${title}` : '';
      const d = duration ? ` — ${duration}` : '';
      const syn = synopsis ? `\nSynopsis: ${synopsis}` : '';
      const ch = Array.isArray(characters) && characters.length
        ? `\nCharacters: ${characters.join(', ')}`
        : '';
      return `Scene ${number}${t}${d}${syn}${ch}`;
    });

    return parts.join('\n\n').trim();
  } catch (e) {
    console.error('[buildScreenplayLite] failed:', e);
    return '';
  }
}

export function summarizeStrictCharacter(entry = {}) {
  const isObj = (v) => v && typeof v === 'object' && !Array.isArray(v);
  const isPrim = (v) => !Array.isArray(v) && !isObj(v);
  const has = (v) => v !== undefined && v !== null && String(v).trim() !== '';
  const list = (v) => Array.isArray(v) ? v.filter((x) => has(x)).join('; ') : (has(v) ? String(v) : '');

  // Pretty labels for known section names; unknowns will be title-cased automatically
  const LABEL_MAP = {
    physical_profile: 'physical_profile',
    hair: 'hair',
    skin: 'skin',
    eyes: 'eyes',
    eyebrows: 'eyebrows',
    jawline: 'jawline',
    cheekbones: 'cheekbones',
    chin: 'chin',
    forehead: 'forehead',
    ears: 'ears',
    nose: 'nose',
    mouth_lips: 'Mouth & Lips',
    facial_hair: 'facial_hair',
    facial_proportions: 'facial_proportions',
    facial_features: 'facial_features',
    distinctive_marks: 'distinctive_marks',
    three_d_face: '3D Face',
    dynamic_features: 'dynamic_features',
  };

  const titleCaseKey = (k) => {
    if (LABEL_MAP[k]) return LABEL_MAP[k];
    return k
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (m) => m.toUpperCase())
      .trim();
  };

  // Render a flat "k: v | k: v" line for an object; include unknown keys automatically
  const renderKVLine = (obj, preferred = []) => {
    if (!isObj(obj)) return has(obj) ? String(obj) : '-';
    const seen = new Set();

    const parts = [];

    // Preferred keys first (if present)
    for (const k of preferred) {
      const v = obj[k];
      if (v === undefined) continue;
      seen.add(k);
      parts.push(`${k}: ${renderValue(v)}`);
    }

    // Then any remaining keys (sorted for determinism)
    const rest = Object.keys(obj)
      .filter((k) => !seen.has(k))
      .sort((a, b) => a.localeCompare(b));

    for (const k of rest) {
      parts.push(`${k}: ${renderValue(obj[k])}`);
    }

    return parts.length ? parts.join(' | ') : '-';
  };

  // Render a value: primitives as text, arrays as list, objects flattened one level deep
  const renderValue = (v) => {
    if (Array.isArray(v)) return list(v) || '-';
    if (isPrim(v)) return has(v) ? String(v) : '-';
    // object: flatten one level (key: value | key: value)
    const inner = Object.keys(v).map((k) => `${k}: ${renderValue(v[k])}`).join(' | ');
    return inner || '-';
  };

  // Physical profile renderer:
  // 1) Show a stable set of headline vitals (labels) if present or as '-'
  // 2) Show canonical sections in a sensible order
  // 3) Append ANY new/unknown sections automatically (sorted), with clean labels
  const renderPhysicalProfile = (pp) => {
    if (!isObj(pp)) return has(pp) ? String(pp) : '';

    const lines = [];

    // Headline vitals (always show, even if missing)
    const vitals = [
      ['Height', pp.height],
      ['Weight', pp.weight],
      ['Build', pp.build],
      ['Age', pp.age],
      ['Gender', pp.gender],
      ['Sex', pp.sex],
      ['species', pp.species],
      ['ethnicity', pp.ethnicity],
      ['race', pp.race],
      ['religion', pp.religion],
      ['nationality', pp.nationality],
    ];
    for (const [label, val] of vitals) {
      lines.push(`${label}: ${has(val) ? String(val) : '-'}`);
    }

    // Canonical sections with preferred inner-key order
    const SECTION_ORDER = [
      'skin', 'hair', 'eyes', 'eyebrows', 'nose', 'mouth_lips',
      'jawline', 'cheekbones', 'chin', 'forehead', 'ears',
      'facial_hair', 'facial_proportions', 'facial_features', 'distinctive_marks',
      'three_d_face', 'dynamic_features'
    ];

    const PREFERRED_INNER = {
      skin: ['tone', 'undertone', 'texture'],
      hair: ['color', 'length', 'style', 'hairline', 'texture', 'density'],
      eyes: ['color', 'shape', 'iris_size', 'space_between_eyes_mm'],
      eyebrows: ['shape', 'thickness', 'arch', 'placement'],
      nose: ['width_mm', 'length_mm', 'curvature', 'bridge_shape', 'bridge_depth'],
      mouth_lips: ['width_mm', 'upper_lip', 'lower_lip', 'color', 'texture'],
      jawline: ['shape', 'width_mm', 'angle_deg'],
      cheekbones: ['structure', 'height', 'prominence'],
      chin: ['contour', 'prominence'],
      forehead: ['shape', 'height'],
      ears: ['shape', 'size', 'angle_deg', 'details'],
      facial_hair: ['presence', 'pattern', 'thickness', 'length', 'style'],
      facial_proportions: ['face_width_mm', 'face_height_mm', 'ratios'],
      three_d_face: ['depth_profile', 'eye_recession', 'nose_bridge_depth'],
      dynamic_features: ['emotion_changes', 'blink_rate'],
    };

    const seenTop = new Set();

    for (const sec of SECTION_ORDER) {
      seenTop.add(sec);
      const val = pp[sec];
      if (sec === 'facial_features' || sec === 'distinctive_marks') {
        const arrLine = Array.isArray(val) && val.length ? val.join('; ') : '-';
        lines.push(`${titleCaseKey(sec)}: ${arrLine}`);
      } else {
        const inner = renderKVLine(isObj(val) ? val : {}, PREFERRED_INNER[sec] || []);
        lines.push(`${titleCaseKey(sec)}: ${inner}`);
      }
    }

    // Append ANY unknown/new sections from backend (auto)
    const extraSections = Object.keys(pp)
      .filter((k) => !seenTop.has(k) && !['height', 'weight', 'age', 'height_cm', 'weight_kg', 'build', 'age_range', 'gender', 'sex', 'species', 'ethnicity', 'race', 'religion', 'nationality'].includes(k))
      .sort((a, b) => a.localeCompare(b));

    for (const k of extraSections) {
      const v = pp[k];
      if (k === 'facial_features' || k === 'distinctive_marks') {
        const arrLine = Array.isArray(v) && v.length ? v.join('; ') : '-';
        lines.push(`${titleCaseKey(k)}: ${arrLine}`);
      } else if (isObj(v)) {
        lines.push(`${titleCaseKey(k)}: ${renderKVLine(v, [])}`);
      } else {
        lines.push(`${titleCaseKey(k)}: ${has(v) ? String(v) : '-'}`);
      }
    }

    return lines.join('\n');
  };

  // ----- Outer summary (unchanged except calling the new renderer) -----
  const ORDER = [
    'bio', 'role', 'arc', 'goals', 'physical_profile', 'wardrobe_and_props',
    'conflicts_flaws', 'backstory', 'relationships',
    'expressions_palette', 'mannerisms', 'voice_and_diction', 'dialogue_style',
    'skills_talents', 'limitations_constraints', 'image_generation_prompt', 'negative_prompt', 'do_not_change'
  ];

  const formatters = {
    goals: list,
    conflicts_flaws: list,
    expressions_palette: list,
    mannerisms: list,
    skills_talents: list,
    limitations_constraints: list,
    do_not_change: list,
    wardrobe_and_props: list,
    relationships: (rel) => {
      if (!isObj(rel)) return list(rel);
      const items = Object.entries(rel).map(([k, v]) => `${k}=${String(v ?? '').trim()}`);
      return items.length ? items.join('; ') : '-';
    },
    physical_profile: renderPhysicalProfile,
  };

  const lines = [];
  for (const key of ORDER) {
    const v = entry[key];
    const exists = key === 'physical_profile' ? true : (isObj(v) ? Object.keys(v).length > 0 : has(v));
    if (!exists) continue;

    // Special handling for physical_profile to avoid the "physical_profile:" prefix duplication
    if (key === 'physical_profile') {
      const pp = renderPhysicalProfile(v || {});
      if (has(pp)) lines.push(pp);
      continue;
    }

    const fmt = formatters[key];
    // Use TitleCase for the key label (Bio, Role, etc.)
    const label = key === 'bio' ? 'Bio' : titleCaseKey(key);
    const value = fmt ? fmt(v || {}) : (isObj(v) ? JSON.stringify(v) : list(v));
    if (has(value)) lines.push(`${label}: ${value}`);
  }

  // Include any unknown top-level keys as a safety net
  for (const key of Object.keys(entry)) {
    if (ORDER.includes(key) || key === 'concise_description' || key === 'image') continue;
    const v = entry[key];
    const value = isObj(v) ? JSON.stringify(v) : list(v);
    if (has(value)) lines.push(`${key}: ${value}`);
  }

  return lines.join('\n');
}


// --- helper: turn strict-JSON screenplay → legacy detailed text (so ALL old code keeps working) ---
export const formatDetailedScreenplayFromJSONStrict = (jsonScreenplay) => {
  if (!jsonScreenplay || !Array.isArray(jsonScreenplay.scenes)) return '';
  const lines = [];
  (jsonScreenplay.scenes || []).forEach((s) => {
    const number = s.number ?? '';
    const title = s.title || `Scene ${number}`;
    const durationMin = (s.duration_minutes != null) ? `${Math.max(1, Math.round(Number(s.duration_minutes)))} min` : '';
    const chars = Array.isArray(s.characters) ? s.characters.join(', ') : '';
    //const vp = Array.isArray(s.visual_prompt) ? s.visual_prompt : (s.visual_prompt ? [String(s.visual_prompt)] : []);
    const beats = Array.isArray(s.beat_summary) ? s.beat_summary : [];
    const shots = Array.isArray(s.shots_per_beat) ? s.shots_per_beat : [];

    const lighting = Array.isArray(s.lighting_plan) ? s.lighting_plan : (s.lighting_plan ? [String(s.lighting_plan)] : []);
    const dialogues = Array.isArray(s.dialogues) ? s.dialogues : [];
    const ce = s.character_expressions && typeof s.character_expressions === 'object'
      ? Object.entries(s.character_expressions).map(([k, v]) => `${k}: ${v}`)
      : [];

    const ca = s.character_attire && typeof s.character_attire === 'object'
      ? Object.entries(s.character_attire).map(([k, v]) => `${k}: ${v}`)
      : [];

    lines.push(`Scene ${number}: ${title}${durationMin ? ` [${durationMin}]` : ''}`);
    if (s.logline) lines.push(`Scene Synopsis: ${s.logline}`);
    if (chars) lines.push(`Characters: ${chars}`);

    //if (vp.length) {
    //  lines.push('Visual Prompt:');
    //  vp.forEach(v => lines.push(`- ${v}`));
    //}

    if (s.visual) {
      lines.push('Visual Prompt:');
      lines.push(s.visual);
    }

    if (s.environment) {
      lines.push('Environment:');
      lines.push(s.environment);
    }

    if (s.scene_bible) {
      const sb = s.scene_bible || {};
      const hasAny =
        sb.lens_mm || sb.aspect || sb.time_of_day || sb.palette || sb.grade ||
        (Array.isArray(sb.continuity_notes) && sb.continuity_notes.length);
      if (hasAny) {
        lines.push('Scene Bible:');
        if (sb.lens_mm) lines.push(`  Lens: ${sb.lens_mm}mm`);
        if (sb.aspect) lines.push(`  Aspect: ${sb.aspect}`);
        if (sb.time_of_day) lines.push(`  Time: ${sb.time_of_day}`);
        if (sb.palette) lines.push(`  Palette: ${sb.palette}`);
        if (sb.grade) lines.push(`  Grade: ${sb.grade}`);
        if (Array.isArray(sb.continuity_notes) && sb.continuity_notes.length) {
          lines.push(`  Continuity: ${sb.continuity_notes.join('; ')}`);
        }
      }
    }
    // NEW: Shot Matrix (compact, per beat — same list style as Shots Per Beat)
    // Prefer camelCase too, but handle snake_case from backend
    const rawShotMatrix =
      (Array.isArray(s.shotMatrix) ? s.shotMatrix : null) ??
      (Array.isArray(s.shot_matrix) ? s.shot_matrix : null);
    if (rawShotMatrix && rawShotMatrix.length) {
      lines.push('Shot Matrix:');
      rawShotMatrix.forEach((row) => {
        const beat = row?.beat != null ? row.beat : '';
        const shots = Array.isArray(row?.shots) ? row.shots : [];
        const total = shots.reduce((acc, sh) => acc + (Number(sh?.duration_sec) || 0), 0);
        // Beat header
        lines.push(`  Beat ${beat} — total ${Math.round(total)}s:`);
        // Each shot with all details preserved
        shots.forEach((sh) => {
          const id = sh?.id ? String(sh.id) : '?';
          const size = sh?.size ? String(sh.size) : '';
          const durS = Number.isFinite(sh?.duration_sec) && sh.duration_sec > 0 ? `${sh.duration_sec}s` : '';
          const bracket = [size, durS].filter(Boolean).join(' ');

          const angleStr = sh?.angle ? `Angle: ${sh.angle}` : '';
          const feelStr = sh?.camera_feel ? `Camera Feel: ${sh.camera_feel}` : '';
          const meta = [angleStr, feelStr].filter(Boolean).join('; ');
          const cont = sh?.init_from_prev_last_frame ? ' • continues' : '';

          // Header: "Shot A [WS 8s] Angle: …; Camera Feel: …"
          lines.push(
            `    • Shot ${id}${bracket ? ` [${bracket}]` : ''}${meta ? ` ${meta}` : ''}${cont}`
          );
          // Optional verbose lines
          if (sh?.prompt) {
            lines.push(`       Prompt: ${sh.prompt}`);
          }
          if (Array.isArray(sh?.micro_motion) && sh.micro_motion.length) {
            lines.push(`       Micro-motion: ${sh.micro_motion.join(', ')}`);
          }
          if (Array.isArray(sh?.audio) && sh.audio.length) {
            lines.push(`       Audio: ${sh.audio.join(', ')}`);
          }
        });
      });
    }

    if (s.coverage_plan && (s.coverage_plan.coverage || s.coverage_plan.transitions || s.coverage_plan.editorial_intent)) {
      lines.push('Coverage Plan:');
      if (s.coverage_plan.coverage) lines.push(`Coverage: ${s.coverage_plan.coverage}`);
      if (s.coverage_plan.transitions) lines.push(`Transitions: ${s.coverage_plan.transitions}`);
      if (s.coverage_plan.editorial_intent) lines.push(`Editorial Intent: ${s.coverage_plan.editorial_intent}`);
    }

    if (lighting.length) {
      lines.push('Lighting Plan:');
      lighting.forEach(l => lines.push(` ${l}`));
    }

    if (s.background_music) {
      lines.push('Background Music:');
      lines.push(s.background_music);
    }

    if (ce.length) {
      lines.push('Character Expressions:');
      ce.forEach(x => lines.push(` ${x}`));
    }

    if (ca.length) {
      lines.push('Character Attire:');
      ca.forEach(x => lines.push(` ${x}`));
    }

    if (dialogues.length) {
      lines.push('Dialogue Script:');
      dialogues.forEach(d => {
        const line = (typeof d === 'string') ? d : `${d.character || ''}: ${d.line || ''}`.trim();
        lines.push(line);
      });
    }

    lines.push('');
  });
  return lines.join('\n');
};


export function formatShotMatrixText(matrix) {
  if (!Array.isArray(matrix) || !matrix.length) return '';
  const lines = ['Shot Matrix'];

  matrix.forEach((row) => {
    const shots = Array.isArray(row?.shots) ? row.shots : [];
    const total = shots.reduce((acc, s) => acc + (Number(s?.duration_sec) || 0), 0);
    lines.push(`\n  • Beat ${row?.beat ?? ''} — total ${Math.round(total)}s:`);

    shots.forEach((sh) => {
      const id = sh?.id ? String(sh.id) : '?';
      const size = sh?.size ? String(sh.size) : '';
      const durS = Number.isFinite(sh?.duration_sec) && sh.duration_sec > 0 ? `${sh.duration_sec}s` : '';
      const bracket = [size, durS].filter(Boolean).join(' ');

      const angleStr = sh?.angle ? `Angle: ${sh.angle}` : '';
      const feelStr = sh?.camera_feel ? `Camera Feel: ${sh.camera_feel}` : '';
      const meta = [angleStr, feelStr].filter(Boolean).join('; ');
      const cont = sh?.init_from_prev_last_frame ? ' · continues' : '';

      // Header line
      lines.push(`  • Shot ${id}${bracket ? ` [${bracket}]` : ''}${meta ? ` ${meta}` : ''}${cont}`);

      if (sh?.prompt) {
        lines.push(`    • Prompt: ${sh.prompt}`);
      }
      if (Array.isArray(sh?.micro_motion) && sh.micro_motion.length) {
        lines.push(`    • Micro-motion: ${sh.micro_motion.join(', ')}`);
      }
      if (Array.isArray(sh?.audio) && sh.audio.length) {
        lines.push(`    • Audio: ${sh.audio.join(', ')}`);
      }
    });
  });

  return lines.join('\n');
}


export function normalizeScenesFromJson(jsonScreenplay) {
  const scenes =
    Array.isArray(jsonScreenplay?.scenes)
      ? jsonScreenplay.scenes
      : (Array.isArray(jsonScreenplay?.screenplay?.scenes)
        ? jsonScreenplay.screenplay.scenes
        : []);

  const objToArray = (obj) => {
    if (!obj || typeof obj !== 'object') return [];
    return Object.entries(obj).map(([k, v]) => `${k}: ${v}`);
  };

  return scenes.map((s, idx) => ({
    // ---- fields consumed directly by toSceneForTiles ----
    number: s?.number ?? idx + 1,
    title: s?.title || `Scene ${s?.number ?? idx + 1}`,
    duration: s?.duration || s?.duration_minutes || '',

    // toSceneForTiles uses summary || synopsis || SceneSynopsis
    summary: s?.summary || s?.synopsis || s?.SceneSynopsis || s?.logline || '',

    // carried through if present
    image: s?.image ?? null,
    video: s?.video ?? null,

    // ---- names/aliases that toSceneForTiles reads into additionalInfo ----
    synopsis: s?.synopsis ?? s?.SceneSynopsis ?? s?.logline ?? '',
    visual: s?.visual ?? (Array.isArray(s?.visuals) ? s.visuals.join('\n') : ''),
    visualPrompt: s?.visualPrompt ?? s?.visual_prompt ?? '',
    sceneImagePrompt: s?.scene_image_prompt || '',
    environment: s?.environment ?? '',

    // arrays expected (safe for UI)
    characterExperessions: objToArray(s?.character_expressions),
    characterAttire: objToArray(s?.character_attire),
    characters: Array.isArray(s?.characters) ? s.characters : [],
    coveragePlan: s?.coverage_plan ?? null,
    lightingPlan:
      typeof s?.lighting_plan === 'string'
        ? s.lighting_plan
        : (Array.isArray(s?.lighting) ? s.lighting.join('\n') : ''),

    // fx passthrough
    fx: s?.fx ?? '',

    // dialogues: toSceneForTiles converts strings → {text, audio:null}
    // Map {character,line} → "CHAR: line" strings so it Just Works.
    dialogues: Array.isArray(s?.dialogues)
      ? s.dialogues.map(d =>
        typeof d === 'string'
          ? d
          : (d?.line ? (d?.character ? `${d.character}: ${d.line}` : d.line) : '')
      ).filter(Boolean)
      : [],

    // music: toSceneForTiles reads backgroundMusic into { text, audio:null }
    backgroundMusic: s?.background_music || s?.music?.cue || '',

    // scene bible + shot matrix (both supported by toSceneForTiles)
    sceneBible: s?.scene_bible ?? null,
    shotMatrix: Array.isArray(s?.shot_matrix) ? s.shot_matrix : [],
  }));
}

export function renderScreenplayForTile(jsonScreenplay) {
  const title = String(jsonScreenplay?.title || '').trim();
  const scenes = Array.isArray(jsonScreenplay?.scenes) ? jsonScreenplay.scenes : [];

  const header = title ? `Title: ${title}\n` : '';
  const body = scenes.map((s) => {
    const n = s?.number != null ? String(s.number) : '';
    const t = s?.title ? String(s.title) : '';
    const slug = s?.slugline ? `\n${s.slugline}` : '';
    const summary = s?.summary ? `\nSummary: ${s.summary}` : '';
    const dur = s?.duration_sec ? `\nDuration: ${s.duration_sec}s` : '';

    // Keep indentation & section labels similar to your current formatter output
    const beats = Array.isArray(s?.beats) && s.beats.length
      ? `\nBeats:\n${s.beats.map((b, i) => `  ${i + 1}. ${b?.text || ''}`).join('\n')}`
      : '';

    const dialogue = Array.isArray(s?.dialogue) && s.dialogue.length
      ? `\nDialogue:\n${s.dialogue.map(d => `  ${d?.character || 'CHAR'}: ${d?.line || ''}`).join('\n')}`
      : '';

    const music = s?.music?.cue ? `\nMusic: ${s.music.cue}` : '';

    const shots = Array.isArray(s?.shot_matrix) && s.shot_matrix.length
      ? `\nShot Matrix:\n${s.shot_matrix.map(row => {
        const beat = row?.beat != null ? `${row.beat}` : '';
        const shotsArr = Array.isArray(row?.shots) ? row.shots : [];
        const shotsTxt = shotsArr.map(sh => {
          const id = sh?.id ? String(sh.id) : 'A';
          const size = sh?.size ? ` ${sh.size}` : '';
          const angle = sh?.angle ? ` ${sh.angle}` : '';
          const feel = sh?.feel ? ` — ${sh.feel}` : '';
          return `    ${id}:${size}${angle}${feel}`;
        }).join('\n');
        return `  Beat ${beat}:\n${shotsTxt}`;
      }).join('\n')}`
      : '';

    return `Scene ${n}${t ? ` — ${t}` : ''}${slug}${summary}${dur}${beats}${dialogue}${music}${shots}`;
  }).join('\n\n');

  return `${header}${body}`.trim();
}


// ---------------- Character name aliasing & matching helpers ----------------

// Titles / boilerplate words that are not identity-bearing for name matching
const CHARACTER_TITLE_WORDS = new Set([
  'the', 'of', 'and',
  'mr', 'mrs', 'ms', 'dr', 'prof',
  'sir', 'lord', 'lady',
  'king', 'queen', 'prince', 'princess',
  'duke', 'duchess', 'earl', 'count', 'countess',
  'house', 'saint', 'st'
]);

// Basic normalization used for all alias keys:
// - lowercase
// - remove parenthetical notes
// - strip punctuation
// - collapse whitespace
function normalizeCharacterAliasKey(raw) {
  if (!raw) return '';
  return String(raw)
    .toLowerCase()
    .replace(/\([^)]*\)/g, ' ')
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

// Generate a set of alias keys for a given bible character name.
// These aliases are *normalized* (lowercase, no punctuation).
// We never mutate the original name; we only expand how it can be referred to.
function buildAliasesForBibleName(name) {
  const aliases = new Set();
  const base = normalizeCharacterAliasKey(name);
  if (!base) return aliases;

  const tokens = base.split(' ').filter(Boolean);
  if (!tokens.length) return aliases;

  // Full normalized name
  aliases.add(base);

  // Drop leading titles (house, lord, etc.)
  let i = 0;
  while (i < tokens.length && CHARACTER_TITLE_WORDS.has(tokens[i])) {
    i++;
  }
  if (i > 0 && i < tokens.length) {
    aliases.add(tokens.slice(i).join(' '));
  }

  // First and last tokens as possible short references
  if (tokens.length >= 1) aliases.add(tokens[0]);
  if (tokens.length >= 2) aliases.add(tokens[tokens.length - 1]);

  // If the last token looks like a simple plural, add singular-style aliases.
  // This is alias *expansion*, not mutation of the stored name.
  const last = tokens[tokens.length - 1];
  if (last && last.length > 4 && last.endsWith('s')) {
    const tail2 = last.slice(-2);
    if (tail2 !== 'us' && tail2 !== 'is' && tail2 !== 'ys' && !last.endsWith('ss')) {
      const singularLast = last.slice(0, -1); // velaryons -> velaryon, celtigars -> celtigar
      aliases.add(singularLast);

      // Name without titles but singularized last token
      const altTokens = tokens.slice();
      altTokens[altTokens.length - 1] = singularLast;
      aliases.add(altTokens.join(' '));
      if (i > 0 && i < tokens.length) {
        const altNoTitles = altTokens.slice(i).join(' ');
        aliases.add(altNoTitles);
      }

      // Common title + family forms
      aliases.add('lord ' + singularLast);
      aliases.add('house ' + singularLast);
      aliases.add('the ' + singularLast);
    }
  }

  return aliases;
}

// Iterate over various bible shapes in a uniform way and invoke a callback.
function forEachCharacterBibleEntry(bible, cb) {
  if (!bible) return;
  if (bible instanceof Map) {
    for (const [name, entry] of bible.entries()) {
      const v = (entry && entry.value) ?? entry ?? {};
      cb(name, v);
    }
    return;
  }
  if (Array.isArray(bible)) {
    for (const row of bible) {
      const name = row?.key || row?.name;
      const v = row?.value ?? {};
      if (name) cb(name, v);
    }
    return;
  }
  if (typeof bible === 'object') {
    for (const [name, entry] of Object.entries(bible)) {
      const v = entry ?? {};
      cb(name, v);
    }
  }
}

// Simple token-overlap similarity between two token lists.
// similarity = overlap / min(|A|, |B|)
function characterTokenSimilarity(aTokens, bTokens) {
  if (!aTokens.length || !bTokens.length) return 0;
  const bSet = new Set(bTokens);
  let overlap = 0;
  for (const t of aTokens) {
    if (bSet.has(t)) overlap += 1;
  }
  const denom = Math.min(aTokens.length, bTokens.length);
  return denom === 0 ? 0 : overlap / denom;
}

// Build alias index for a given bible.
// Returns { entries, aliasToIndexes }
function buildCharacterAliasIndex(bible) {
  const entries = [];
  const aliasToIndexes = new Map();

  forEachCharacterBibleEntry(bible, (name, value) => {
    if (!name) return;
    const baseKey = normalizeCharacterAliasKey(name);
    const tokens = baseKey.split(' ').filter(Boolean);
    const idx = entries.length;
    entries.push({ name, value, baseKey, tokens });

    const aliases = buildAliasesForBibleName(name);
    for (const alias of aliases) {
      if (!alias) continue;
      const key = alias; // aliases are already normalized
      if (!aliasToIndexes.has(key)) {
        aliasToIndexes.set(key, []);
      }
      aliasToIndexes.get(key).push(idx);
    }
  });

  return { entries, aliasToIndexes };
}

// Resolve a requested scene name to the best matching bible entry using the alias index.
function resolveCharacterFromAliasIndex(index, requestedName) {
  if (!index || !requestedName) return null;

  const { entries, aliasToIndexes } = index;
  const norm = normalizeCharacterAliasKey(requestedName);
  if (!norm) return null;

  const tokens = norm.split(' ').filter(Boolean);
  if (!tokens.length) return null;

  const candidateAliases = new Set();

  // Start with the full normalized form.
  candidateAliases.add(norm);

  // Drop leading titles for the request as well.
  let i = 0;
  while (i < tokens.length && CHARACTER_TITLE_WORDS.has(tokens[i])) {
    i++;
  }
  if (i > 0 && i < tokens.length) {
    candidateAliases.add(tokens.slice(i).join(' '));
  }

  // First and last tokens as fallbacks.
  if (tokens.length >= 1) candidateAliases.add(tokens[0]);
  if (tokens.length >= 2) candidateAliases.add(tokens[tokens.length - 1]);

  // If request last token looks plural, add singular-like forms.
  const last = tokens[tokens.length - 1];
  if (last && last.length > 4 && last.endsWith('s')) {
    const tail2 = last.slice(-2);
    if (tail2 !== 'us' && tail2 !== 'is' && tail2 !== 'ys' && !last.endsWith('ss')) {
      const singularLast = last.slice(0, -1);
      candidateAliases.add(singularLast);
      const altTokens = tokens.slice();
      altTokens[altTokens.length - 1] = singularLast;
      candidateAliases.add(altTokens.join(' '));
      if (i > 0 && i < tokens.length) {
        const altNoTitles = altTokens.slice(i).join(' ');
        candidateAliases.add(altNoTitles);
      }
      candidateAliases.add('lord ' + singularLast);
      candidateAliases.add('house ' + singularLast);
      candidateAliases.add('the ' + singularLast);
    }
  }

  // 1) Try alias hits in order of specificity.
  let best = null;
  let bestScore = 0;
  const THRESHOLD = 0.6;

  for (const alias of candidateAliases) {
    const idxs = aliasToIndexes.get(alias);
    if (!idxs || idxs.length === 0) continue;

    if (idxs.length === 1) {
      const e = entries[idxs[0]];
      return { name: e.name, value: e.value };
    }

    // Multiple candidates for this alias -> choose by token similarity.
    for (const idx of idxs) {
      const e = entries[idx];
      const score = characterTokenSimilarity(tokens, e.tokens);
      if (score > bestScore) {
        bestScore = score;
        best = e;
      }
    }
  }

  if (best && bestScore >= THRESHOLD) {
    return { name: best.name, value: best.value };
  }

  // 2) Fallback: compare against all entries by token similarity.
  for (const e of entries) {
    const score = characterTokenSimilarity(tokens, e.tokens);
    if (score > bestScore) {
      bestScore = score;
      best = e;
    }
  }

  if (!best || bestScore < THRESHOLD) return null;
  return { name: best.name, value: best.value };
}


// Build a normalized character map from various shapes of "bible".
// If onlyNames is provided (array of strings), include ONLY those characters
// by resolving each scene name to its best matching bible entry.
// If onlyNames is empty, return all bible entries (backwards compatible).
export function normalizeCharacterBible(bible, onlyNames = []) {
  if (!bible) return {};

  const wantAll = !onlyNames || onlyNames.length === 0;

  // Helper to normalize one bible entry into the output shape
  const makeEntry = (value) => {
    const strict = (value && value._strict) || {};
    return {
      ...value,
      concise_description:
        (value && (value.concise_description || value.conciseDescription)) ||
        strict.concise_description ||
        (value && value.description) ||
        "",
      imageUrl:
        (value && (value.imageUrl || value.image)) ||
        strict.imageUrl ||
        strict.image ||
        null,
    };
  };

  // 1) No filter list -> return all entries (backwards compatible)
  if (wantAll) {
    const out = {};
    forEachCharacterBibleEntry(bible, (name, v) => {
      if (!name) return;
      out[name] = makeEntry(v);
    });
    return out;
  }

  // 2) Filtered list -> resolve each requested name via alias index
  const index = buildCharacterAliasIndex(bible);
  const out = {};
  const added = new Set();

  for (const rawName of onlyNames) {
    if (!rawName) continue;
    const match = resolveCharacterFromAliasIndex(index, rawName);
    if (!match) continue;

    const { name, value } = match;
    if (added.has(name)) continue;

    out[name] = makeEntry(value);
    added.add(name);
  }

  return out;
}







/* =========================================================================
 * JSON → Full editable text (used in Edit mode and raw preview)
 * ========================================================================= */
export function renderDetailedScreenplayText(js) {
  if (!js || typeof js !== 'object') return '';
  const IND = (n) => '  '.repeat(n);
  const join = (a) => Array.isArray(a) ? a.join(', ') : (a || '');

  const header = js.title ? `Title: ${js.title}\n\n` : '';
  const scenes = (Array.isArray(js.scenes) ? js.scenes : []).map((s, idx) => {
    const n = s?.number ?? (idx + 1);
    const t = s?.title ? `Scene ${n} — ${s.title}` : `Scene ${n}`;
    const dur = (typeof s?.duration_minutes === 'number' && s.duration_minutes > 0)
      ? `\nDuration: ${Math.max(1, Math.round(s.duration_minutes))} min`
      : '';

    const bible = s?.scene_bible ? [
      'Scene Bible:',
      s.scene_bible.lens_mm ? `${IND(1)}• Lens: ${s.scene_bible.lens_mm}mm` : null,
      s.scene_bible.aspect ? `${IND(1)}• Aspect: ${s.scene_bible.aspect}` : null,
      s.scene_bible.time_of_day ? `${IND(1)}• Time: ${s.scene_bible.time_of_day}` : null,
      s.scene_bible.palette ? `${IND(1)}• Palette: ${s.scene_bible.palette}` : null,
      s.scene_bible.grade ? `${IND(1)}• Grade: ${s.scene_bible.grade}` : null,
      Array.isArray(s.scene_bible.continuity_notes) && s.scene_bible.continuity_notes.length
        ? `${IND(1)}• Continuity: ${s.scene_bible.continuity_notes.join('; ')}`
        : null,
    ].filter(Boolean).join('\n') : '';

    const visual = s?.visual ? `\n\nVisual Prompt\n${s.visual}` : '';
    const sceneImg = s?.scene_image_prompt
      ? `\n\nScene Image Prompt\n${s.scene_image_prompt}`
      : '';
    const env = s?.environment ? `\n\nEnvironment\n${s.environment}` : '';
    const cov = s?.coverage_plan;
    const coverage = cov && (cov.coverage || cov.transitions || cov.editorial_intent || cov.editorialIntent)
      ? [
        'Coverage Plan:',
        cov.coverage ? `${IND(1)}• Coverage: ${cov.coverage}` : null,
        cov.transitions ? `${IND(1)}• Transitions: ${cov.transitions}` : null,
        (cov.editorial_intent || cov.editorialIntent) ? `${IND(1)}• Editorial Intent: ${cov.editorial_intent || cov.editorialIntent}` : null,
      ].filter(Boolean).join('\n')
      : '';
    const lighting = s?.lighting_plan
      ? `\n\nLighting Plan\n${s.lighting_plan}`
      : (Array.isArray(s?.lighting) && s.lighting.length ? `\n\nLighting Plan\n${s.lighting.join('\n')}` : '');

    const bgm = s?.background_music || (s?.music?.cue || '');
    const music = bgm ? `\n\nBackground Music\n${bgm}` : '';

    const expr = s?.character_expressions && typeof s.character_expressions === 'object'
      ? `\n\nCharacter Expressions\n${Object.entries(s.character_expressions).map(([k, v]) => `• ${k}: ${v}`).join('\n')}`
      : '';

    const attire = s?.character_attire && typeof s.character_attire === 'object'
      ? `\n\nCharacter Attire\n${Object.entries(s.character_attire).map(([k, v]) => `• ${k}: ${v}`).join('\n')}`
      : '';

    const dialogs = Array.isArray(s?.dialogues || s?.dialogue)
      ? `\n\nDialogues\n${(s.dialogues || s.dialogue).map(d =>
        typeof d === 'string' ? `• ${d}` : `• ${[d?.character, d?.line].filter(Boolean).join(': ')}`
      ).join('\n')
      }`
      : '';

    const mx = Array.isArray(s?.shot_matrix) ? s.shot_matrix : [];
    const mxText = mx.length
      ? `\n\nShot Matrix\n${mx.map((row, i2) => {
        const beat = row?.beat ?? (i2 + 1);
        const shots = Array.isArray(row?.shots) ? row.shots : [];
        const lines = shots.map((sh, k) => {
          const id = sh?.id != null ? String(sh.id) : String.fromCharCode('A'.charCodeAt(0) + k);
          const main = [sh?.size, sh?.angle].filter(Boolean).join(', ');
          const feel = sh?.camera_feel ? ` — ${sh.camera_feel}` : '';
          const lens = sh?.lens_mm ? ` (${sh.lens_mm}mm)` : '';
          const dur = (typeof sh?.duration_sec === 'number' && sh.duration_sec > 0) ? ` [${sh.duration_sec}s]` : '';
          const head = `Shot ${id}${(main || feel || lens || dur) ? `: ${main}${feel}${lens}${dur}` : ''}`;
          const bullets = [
            sh?.prompt ? `${IND(2)}• Prompt: ${sh.prompt}` : null,
            Array.isArray(sh?.audio) && sh.audio.length ? `${IND(2)}• Audio: ${join(sh.audio)}` : null,
            Array.isArray(sh?.micro_motion) && sh.micro_motion.length ? `${IND(2)}• Micro-motion: ${join(sh.micro_motion)}` : null,
          ].filter(Boolean).join('\n');
          return `${IND(1)}${head}\n${bullets}`.trimEnd();
        }).join('\n');
        return `Beat ${beat}:\n${lines}`;
      }).join('\n\n')
      }`
      : '';

    return [
      t, dur,
      bible ? `\n${bible}` : '',
      visual, env,
      sceneImg,
      coverage ? `\n\n${coverage}` : '',
      lighting, music, expr, attire, dialogs,
      mxText
    ].filter(Boolean).join('\n');
  }).join('\n\n');

  return `${header}${scenes}`.trim() + '\n';
}
